import { Plus, Trash } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { emptyPriceTierRow, type PriceTierRow } from './products-payload';

/**
 * Editor de la tabla de precios por cantidad (`tablaPrecios`, obligatoria en el contrato).
 *
 * Cada fila dice "a partir de N unidades, el precio unitario es X": así el bot puede vender al
 * por mayor sin inventar descuentos. Los precios se escriben como números (el negocio no tiene
 * moneda configurada, ver `products-format.ts`), sin símbolos.
 *
 * Siempre queda al menos una fila: si se borra la última, se repone una vacía (el backend exige
 * un rango, así que el formulario nunca se ve sin tabla).
 */

export interface PriceTierFieldsProps {
  rows: PriceTierRow[];
  onChange: (rows: PriceTierRow[]) => void;
  disabled?: boolean;
  className?: string;
}

export function PriceTierFields({
  rows,
  onChange,
  disabled = false,
  className,
}: PriceTierFieldsProps) {
  function updateRow(id: string, patch: Partial<PriceTierRow>) {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  function removeRow(id: string) {
    const next = rows.filter((row) => row.id !== id);
    onChange(next.length > 0 ? next : [emptyPriceTierRow()]);
  }

  return (
    <div className={className}>
      <div className="mb-1.5 flex items-center gap-3">
        <span className="flex-1 text-caption font-medium text-ink-soft">Cantidad desde</span>
        <span className="flex-1 text-caption font-medium text-ink-soft">Precio unitario</span>
        {/* Espacio del botón de eliminar, para que las dos columnas queden alineadas. */}
        <span className="w-9" aria-hidden="true" />
      </div>

      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={row.id} className="flex items-center gap-3">
            <Input
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={row.cantidad}
              placeholder="ej. 1"
              disabled={disabled}
              aria-label={`Cantidad desde, rango ${index + 1}`}
              onChange={(event) => updateRow(row.id, { cantidad: event.target.value })}
            />

            <Input
              type="number"
              min={0}
              step="0.01"
              inputMode="decimal"
              value={row.precio}
              placeholder="ej. 25000"
              disabled={disabled}
              aria-label={`Precio unitario, rango ${index + 1}`}
              onChange={(event) => updateRow(row.id, { precio: event.target.value })}
            />

            <Button
              variant="ghost"
              size="icon"
              disabled={disabled}
              aria-label={`Eliminar el rango ${index + 1}`}
              onClick={() => removeRow(row.id)}
            >
              <Trash size={16} />
            </Button>
          </div>
        ))}
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="mt-2"
        disabled={disabled}
        leftIcon={<Plus size={14} />}
        onClick={() => onChange([...rows, emptyPriceTierRow()])}
      >
        Agregar rango
      </Button>
    </div>
  );
}
